import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaSearch, FaUserCircle } from 'react-icons/fa';
import { useApp } from '../App';
import './Header.css';

const HINTS = [
  'Italian chef for wedding...',
  'housekeeper in Kigali...',
  'receptionist for hotel...',
  'cleaner for office...',
  'pastry chef...',
  'corporate event chef...'
];

function useTypingHint() {
  const [hintIdx, setHintIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = HINTS[hintIdx];
    const speed = deleting ? 35 : 75;
    const timeout = setTimeout(() => {
      if (!deleting) {
        if (charIdx < current.length) setCharIdx(charIdx + 1);
        else setTimeout(() => setDeleting(true), 1300);
      } else {
        if (charIdx > 0) setCharIdx(charIdx - 1);
        else { setDeleting(false); setHintIdx((hintIdx + 1) % HINTS.length); }
      }
    }, speed);
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, hintIdx]);

  return HINTS[hintIdx].slice(0, charIdx);
}

function Header() {
  const [open, setOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [q, setQ] = useState('');
  const [mobileQ, setMobileQ] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const { setShowRequest, setShowTrack, setShowLogin, manager } = useApp();
  const navigate = useNavigate();

  const hintDesktop = useTypingHint();
  const hintMobile = useTypingHint();

  /* ---------- Scroll detection ---------- */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    navigate(`/search?q=${encodeURIComponent(term)}`);
    setQ('');
  };

  const handleMobileSearch = (e) => {
    e.preventDefault();
    const term = mobileQ.trim();
    if (!term) return;
    navigate(`/search?q=${encodeURIComponent(term)}`);
    setMobileQ('');
    setMobileSearch(false);
  };

  return (
    <header className={`header-root ${scrolled ? 'header-scrolled' : ''}`}>
      <div className="header-inner">
        <Link to="/" className="header-logo" aria-label="Hospify Link home">
          {/* Logo image — full color, visible at top */}
          <img
            src="/Logo.png"
            alt="Hospify Logo"
            className={`header-logo-img ${scrolled ? 'header-logo-img-hidden' : ''}`}
          />
          {/* Text — shown on scroll */}
          <span className={`header-logo-text ${scrolled ? 'header-logo-text-visible' : ''}`}>
            HOSPIFY <span className="header-logo-accent">LINK</span>
          </span>
        </Link>

        <nav className={`header-nav ${open ? 'header-nav-open' : ''}`}>
          <NavLink to="/" end className="header-link" onClick={() => setOpen(false)}>Home</NavLink>
          <NavLink to="/about" className="header-link" onClick={() => setOpen(false)}>About</NavLink>
          <NavLink to="/contact" className="header-link" onClick={() => setOpen(false)}>Contact</NavLink>
          <NavLink to="/gallery" className="header-link" onClick={() => setOpen(false)}>Gallery & Recipes</NavLink>
          <NavLink to="/professionals" className="header-link" onClick={() => setOpen(false)}>Professionals</NavLink>
          <button className="header-link header-btn-link" onClick={() => { setShowRequest(true); setOpen(false); }}>Make Request</button>
          <button className="header-link header-btn-link" onClick={() => { setShowTrack(true); setOpen(false); }}>Track Request</button>
        </nav>

        <div className="header-actions">
          <form className="header-search" onSubmit={handleSearch}>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${hintDesktop}`} aria-label="Search" />
            <button type="submit" aria-label="Submit search"><FaSearch /></button>
          </form>

          <button className="header-search-mobile" onClick={() => setMobileSearch(true)} aria-label="Open search">
            <FaSearch />
          </button>

          <button className="header-login" onClick={() => setShowLogin(true)}
            title={manager ? 'Dashboard' : 'Manager Login'}>
            <FaUserCircle /> 
          </button>

          <button className="header-burger" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {mobileSearch && (
        <div className="header-search-modal" onClick={() => setMobileSearch(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={handleMobileSearch} className="header-search-modal-inner">
            <FaSearch className="header-search-modal-icon" />
            <input autoFocus value={mobileQ} onChange={e => setMobileQ(e.target.value)}
              placeholder={`Search ${hintMobile}`} />
            <button type="submit" aria-label="Search"><FaSearch /></button>
          </form>
        </div>
      )}
    </header>
  );
}

export default Header;
